import { ApiErrorResponse } from "@/@types/exception-list";
import { EngineExceptionEnum } from "@/enum/exception-list";
import {
  useCreateExportModelEngineMutation,
  useGetExceptionListQuery,
} from "@/services/exception-list";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { Load } from "components/load";
import PageError from "components/page-error";
import { Button } from "components/ui/button";
import { toast } from "components/ui/use-toast";
import { Download, RefreshCw, Upload } from "lucide-react";
import React, { useState } from "react";
import { ExportDialog } from "../components/modal/export-dialog";
import { ImportFeedbackModal } from "../components/modal/import-feedback-modal";
import { getColumns } from "../components/table/columns";
import { DataTable } from "../components/table/data-table";
import { useFilterActions } from "../ducks/hooks/action";
import { useFilterState } from "../ducks/hooks/state";
import { ImportDialog } from "../components/modal/import-dialog";

export const ExceptionList: React.FC = () => {
  const [type, setType] = useState<"NCM" | "PRODUCT">("NCM");
  const filterState = useFilterState();
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenImport, setIsOpenImport] = useState(false);
  const [feedbackData, setFeedbackData] = useState<{
    isOpen: boolean;
    successCount: number;
    errorCount: number;
    totalLines: number;
  }>({ isOpen: false, successCount: 0, errorCount: 0, totalLines: 0 });
  const { updateFilter, clearFilters } = useFilterActions();

  const [geneateModel, { isLoading: isLoadingModel }] =
    useCreateExportModelEngineMutation();

  const handleTypeChange = (value: "NCM" | "PRODUCT") => {
    setType(value);
    updateFilter("type", value);
    clearFilters({ type: value });
  };

  const baseFilters = {
    type: type as EngineExceptionEnum,
    meta: {
      page: filterState.filters.meta?.page || 1,
      perPage: filterState.filters.meta?.perPage || 10,
    },
  };

  const dynamicFilters = Object.entries(filterState.filters).reduce(
    (acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>,
  );

  const filters = { ...baseFilters, ...dynamicFilters };
  const {
    data: exception,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetExceptionListQuery(filters);

  const { data, meta } = exception || {
    data: [],
    meta: { page: 1, perPage: 10, total: 0, totalPages: 0 },
  };
  const filteredData = data.filter((item) => item.type === type);

  const handleOpenModal = () => setIsOpen(true);
  const handleOpenModalImport = () => setIsOpenImport(true);
  const generateModelExcel = async () => {
    try {
      const result = await geneateModel().unwrap();
      const link = document.createElement("a");
      link.href = result.excelBase64;
      link.download = "modelo_exceção.xlsx";
      link.click();
      toast({
        variant: "success",
        description: "Modelo de exceção gerado com sucesso",
        duration: 5000,
      });
    } catch (error) {
      const err = error as { data: ApiErrorResponse };
      toast({
        variant: "destructive",
        description:
          err.data.messages?.[0] || "Erro inesperado ao gerar relatório.",
        duration: 5000,
      });
    }
  };

  if (isLoading) return <Load />;
  if (error) return <PageError status={500} message="Erro ao buscar dados" />;
  return (
    <PageLayout>
      <PageTitle
        title="Exceções de Tributos"
        description="Gerencie as exceções de tributos aplicadas aos seus produtos ou NCMs. Adicione, edite ou remova regras conforme necessário para garantir a conformidade fiscal."
      />
      <ContentContainer>
        <div className="flex flex-row gap-2 justify-end mb-2">
          <Button
            className="px-4 py-2 border border-gray-300 flex items-center gap-2 rounded-md
          text-gray-700 bg-white
          transition-all duration-300 ease-in-out
          hover:bg-gray-100 hover:border-gray-400
          focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className="w-5 h-5" />
            Atualizar dados
          </Button>
          <Button
            className="w-48 h-10"
            onClick={generateModelExcel}
            disabled={isLoadingModel}
          >
            {isLoadingModel ? "Baixando..." : "Download do Modelo"}
          </Button>
        </div>
        <DataTable
          columns={getColumns(type)}
          data={filteredData}
          meta={meta}
          type={type}
          onTypeChange={handleTypeChange}
          isLoading={isFetching}
        />
      </ContentContainer>
      <div className="flex flex-row gap-2 justify-end w-full">
        <Button
          className="flex flex-row gap-2 mt-5 w-48"
          onClick={handleOpenModal}
        >
          <Download className="w-5 h-5" /> Exportar Regras
        </Button>

        {isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="relative bg-white rounded-lg shadow-lg">
              <ExportDialog onCloseModal={() => setIsOpen(false)} />
            </div>
          </div>
        )}

        <Button
          className="flex flex-row gap-2 mt-5 w-48"
          onClick={handleOpenModalImport}
        >
          <Upload className="w-5 h-5" /> Importar Regras
        </Button>

        {isOpenImport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="relative bg-white rounded-lg shadow-lg">
              <ImportDialog
                onCloseModal={() => setIsOpenImport(false)}
                onShowFeedback={(data) => {
                  setFeedbackData({ isOpen: true, ...data });
                }}
              />
            </div>
          </div>
        )}

        <ImportFeedbackModal
          isOpen={feedbackData.isOpen}
          onClose={() =>
            setFeedbackData({
              isOpen: false,
              successCount: 0,
              errorCount: 0,
              totalLines: 0,
            })
          }
          successCount={feedbackData.successCount}
          errorCount={feedbackData.errorCount}
          totalLines={feedbackData.totalLines}
        />
      </div>
    </PageLayout>
  );
};
